// src/pages/InvestorDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { manualData_INVESTOR_METRICS } from "../../constants/investorsDashboard";
import { apiFetchOutStandingRewardPointsBal } from "../../api/backend/investors";
import { ImgTopBtn, GlassBtn } from "../../components/buttons";
import {
  LineIntersectingCircleIcon,
  LineChartReportIcon,
  LineChartIcon,
} from "../../components/icons";

// Helper to format USD in compact notation (e.g. $1.2M)
const formatCompactUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    compactDisplay: "short",
  }).format(n);

// Helper to format percentages
const formatPercent = (n) => `${n.toFixed(1)}%`;

export default function InvestorDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState("capTable");
  const [rawData, setRawData] = useState(manualData_INVESTOR_METRICS);
  const [error, setError] = useState("");

  // Fetch reward-points liability only
  useEffect(() => {
    async function loadRewardPoints() {
      try {
        const res = await apiFetchOutStandingRewardPointsBal();
        const pts = Number(res.data.rewardPointsSum) || 0;
        setRawData((prev) => ({ ...prev, rewardPointsSum: pts }));
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load data");
      }
    }
    loadRewardPoints();
  }, []);

  if (error) return <p style={{ color: "red", fontSize: "1rem" }}>{error}</p>;

  // Compute key investor metrics
  const revenueGrowth =
    rawData.priorQuarterRevenue > 0
      ? ((rawData.currentQuarterRevenue - rawData.priorQuarterRevenue) /
          rawData.priorQuarterRevenue) *
        100
      : 0;
  const mrr = rawData.currentMRR || 0;
  const churnRate = rawData.churnRate || 0;
  const ltvToCac = rawData.cac > 0 ? rawData.lifetimeValue / rawData.cac : 0;
  const grossProfitMargin =
    rawData.revenue > 0
      ? ((rawData.revenue - rawData.costOfGoodsSold) / rawData.revenue) * 100
      : 0;
  const ebitdaMargin =
    rawData.revenue > 0 ? (rawData.ebitda / rawData.revenue) * 100 : 0;
  const cashRunway =
    rawData.monthlyBurn > 0 ? rawData.cashBalance / rawData.monthlyBurn : 0;
  const pointsRaw = rawData.rewardPointsSum || 0;
  const pointsLiability = pointsRaw * 0.01;

  // Cap table rows
  const shareRows = rawData.shareholders.map((sh, i) => {
    const pct = rawData.totalShares
      ? (sh.shares / rawData.totalShares) * 100
      : 0;
    return (
      <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
        <td style={{ padding: "8px" }}>{sh.name}</td>
        <td style={{ padding: "8px" }}>{sh.shares.toLocaleString()}</td>
        <td style={{ padding: "8px" }}>{sh.classType}</td>
        <td style={{ padding: "8px" }}>{pct.toFixed(2)}%</td>
      </tr>
    );
  });
  shareRows.push(
    <tr key="total" style={{ fontWeight: "bold" }}>
      <td style={{ padding: "8px" }}>Total</td>
      <td style={{ padding: "8px" }}>{rawData.totalShares.toLocaleString()}</td>
      <td style={{ padding: "8px" }}></td>
      <td style={{ padding: "8px" }}>100.00%</td>
    </tr>
  );

  // Pro forma rows
  const proFormaRows = rawData.proForma.map((pf) => {
    const totalExpenses =
      pf.accountsPayable +
      pf.wagesPayable +
      pf.travelExpenses +
      pf.trademarkAttorneyFees +
      pf.serverlessCost +
      pf.rdsCost +
      pf.hostingCost +
      pf.ambassadorCost;
    const netProfit = pf.revenue - totalExpenses;
    const profitMargin = pf.revenue > 0 ? (netProfit / pf.revenue) * 100 : 0;
    return (
      <tr key={pf.year} style={{ borderBottom: "1px solid #eee" }}>
        <td style={{ padding: "8px" }}>{pf.year}</td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(pf.revenue)}</td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.accountsPayable)}
        </td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(pf.wagesPayable)}</td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.travelExpenses)}
        </td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.trademarkAttorneyFees)}
        </td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.serverlessCost)}
        </td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(pf.rdsCost)}</td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(pf.hostingCost)}</td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.ambassadorCost)}
        </td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(netProfit)}</td>
        <td style={{ padding: "8px" }}>{formatPercent(profitMargin)}</td>
        <td style={{ padding: "8px" }}>{formatCompactUSD(pointsLiability)}</td>
        <td style={{ padding: "8px" }}>
          {formatCompactUSD(pf.revenue - totalExpenses - pointsLiability)}
        </td>
      </tr>
    );
  });

  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h1 style={{ fontSize: "1.2rem", margin: 0 }}>Investor Dashboard</h1>
        <ImgTopBtn
          image={<LineIntersectingCircleIcon width="25" height="25" />}
          imgTopBtnWidth="50%"
          imgTopBtnTextFontSize="1.3rem"
          imgTopBtnFontColor="#b03a2e"
          imgTopBtnText="Logout"
          onClick={() => {
            localStorage.clear();
            navigate("/", { replace: true });
          }}
        />
      </div>

      {/* Key Metrics */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        {[
          ["Revenue Growth", formatPercent(revenueGrowth)],
          ["MRR", formatCompactUSD(mrr)],
          ["Churn Rate", formatPercent(churnRate)],
          ["LTV:CAC Ratio", ltvToCac.toFixed(1) + "x"],
          ["Gross Profit Margin", formatPercent(grossProfitMargin)],
          ["EBITDA Margin", formatPercent(ebitdaMargin)],
          ["Cash Runway", cashRunway.toFixed(1) + " mo"],
        ].map(([title, value]) => (
          <div
            key={title}
            style={{
              flex: 1,
              padding: 12,
              border: "1px solid #eee",
              borderRadius: 8,
            }}
          >
            <h4 style={{ margin: 0 }}>{title}</h4>
            <p style={{ fontSize: "1.5rem", margin: "4px 0" }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Reward Points Liability */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: 12,
            border: "1px solid #eee",
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h4 style={{ margin: 0 }}>Reward-Points Liability</h4>
          <p style={{ fontSize: "1.5rem", margin: "4px 0" }}>
            {formatCompactUSD(pointsLiability)}
          </p>
          <p style={{ margin: 0, fontSize: "0.875rem", opacity: 0.7 }}>
            ({pointsRaw.toLocaleString()} points)
          </p>
        </div>
      </div>

      {/* Trend Chart */}
      <div style={{ width: "100%", height: "300px", marginBottom: "32px" }}>
        <ResponsiveContainer>
          <LineChart
            data={rawData.mrrHistory}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" unit="%" />
            <Tooltip />
            <ReferenceLine yAxisId="left" y={0} stroke="#000" />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="mrr"
              stroke="var(--color-yellow)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="churnRate"
              stroke="var(--color-darkGold)"
              strokeWidth={2}
              dot
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Toggle Views */}
      <div style={{ display: "flex" }}>
        <GlassBtn
          glassBtnImage={
            <LineChartIcon
              height="15px"
              width="15px"
              fillColor={
                view === "capTable"
                  ? "var(--color-offWhite)"
                  : "var(--color-yellow)"
              }
            />
          }
          glassHoverStyle={{
            height: "55px",
          }}
          className="glass-btn"
          glassRefClassName="adsManagerGlassBtnRef"
          glassBtnText="Cap Table"
          glassBtnHeight="20px"
          glassBtnWidth="150px"
          glassBtnTextFontSize="1rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={
            view === "capTable"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          glassBtnTextDecorationColor={
            view === "capTable"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          onClick={() => setView("capTable")}
          hi2Style={{
            transform: "rotate(90deg) translate(-60px, 75px)",
            zIndex: 9,
          }}
          hi3Style={{
            transform: "rotate(90deg) translate(-60px, -75px)",
            zIndex: 9,
          }}
        />

        <GlassBtn
          glassBtnImage={
            <LineChartReportIcon
              height="15px"
              width="15px"
              fillColor={
                view === "proForma"
                  ? "var(--color-offWhite)"
                  : "var(--color-yellow)"
              }
            />
          }
          className="glass-btn"
          glassHoverStyle={{
            height: "55px",
          }}
          glassBtnText="Pro Forma"
          glassBtnHeight="20px"
          glassBtnWidth="150px"
          glassBtnTextFontSize="1rem"
          glassBtnFontWeight="900"
          glassBtnFontColor={
            view === "proForma"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          glassBtnTextDecorationColor={
            view === "proForma"
              ? "var(--color-offWhite)"
              : "var(--color-yellow)"
          }
          onClick={() => setView("proForma")}
          hi2Style={{
            transform: "rotate(90deg) translate(-60px, 75px)",
            zIndex: 9,
          }}
          hi3Style={{
            transform: "rotate(90deg) translate(-60px, -75px)",
            zIndex: 9,
          }}
        />
      </div>
      {view === "capTable" && (
        <div style={{ marginBottom: "32px" }}>
          <h2 style={{ marginBottom: "8px" }}>Shareholder Register</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 8 }}>Name</th>
                <th style={{ textAlign: "left", padding: 8 }}>Shares</th>
                <th style={{ textAlign: "left", padding: 8 }}>Class</th>
                <th style={{ textAlign: "left", padding: 8 }}>Ownership</th>
              </tr>
            </thead>
            <tbody>{shareRows}</tbody>
          </table>
        </div>
      )}

      {view === "proForma" && (
        <div>
          <h2 style={{ marginBottom: "8px" }}>Pro Forma Projections</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: 8 }}>Year</th>
                <th style={{ padding: 8 }}>Revenue</th>
                <th style={{ padding: 8 }}>Acct Payable</th>
                <th style={{ padding: 8 }}>Wages Payable</th>
                <th style={{ padding: 8 }}>Travel Expenses</th>
                <th style={{ padding: 8 }}>Trademark/Attorney Fees</th>
                <th style={{ padding: 8 }}>Serverless Cost</th>
                <th style={{ padding: 8 }}>RDS Cost</th>
                <th style={{ padding: 8 }}>Hosting Cost</th>
                <th style={{ padding: 8 }}>Ambassador Cost</th>
                <th style={{ padding: 8 }}>Net Profit</th>
                <th style={{ padding: 8 }}>Profit Margin</th>
                <th style={{ padding: 8 }}>Points Liability</th>
                <th style={{ padding: 8 }}>Owner’s Equity</th>
              </tr>
            </thead>
            <tbody>{proFormaRows}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
